import type { OrderType } from 'apiConnector/blotters';
import { FC } from 'react';
import type { CancellationStatus } from 'state/secondaryMarket/shared/cancellation/cancellation.model';

import { AlertBanner } from '../../../components/shared/AlertBanner/AlertBanner';
import { ConfirmationBanner } from '../../../components/shared/ConfirmationBanner/ConfirmationBanner';

interface SecondaryOrderCancellationWorkflowProps {
  orderId: string;
  isin?: string;
  orderType?: OrderType;
  status: CancellationStatus | undefined;
  error: string | undefined;
  onCancellationWorkflowConfirmed: (orderId: string, isin?: string) => void;
  onCancellationWorkflowAborted: (orderId: string) => void;
  onCancellationWorkflowCompleted: (orderId: string) => void;
}

export const SecondaryOrderCancellationWorkflow: FC<SecondaryOrderCancellationWorkflowProps> = ({
  orderId,
  orderType,
  status,
  error,
  isin,
  onCancellationWorkflowConfirmed,
  onCancellationWorkflowAborted,
  onCancellationWorkflowCompleted,
}) => {
  switch (status) {
    case 'INIT': {
      return (
        <ConfirmationBanner
          icon="report_problem"
          e2e="order-cancellation-confirmation-banner"
          color="danger"
          className="position-absolute bottom-0 start-0 end-0"
          message="secondaryMarketOrderBlotter.cancellation.message.confirm"
          confirmButtonText="form.Yes"
          cancelButtonText="form.No"
          onCancel={() => onCancellationWorkflowAborted(orderId)}
          onConfirm={() => onCancellationWorkflowConfirmed(orderId, isin)}
        />
      );
    }
    case 'IN_PROGRESS': {
      return (
        <AlertBanner
          spin
          e2e="order-cancellation-alert-banner-in-progress"
          color="filling-danger"
          className="position-absolute bottom-0 start-0 end-0"
          messages={[
            orderType === 'Live'
              ? 'secondaryMarketOrderBlotter.cancellation.liveTrade.message.inProgress'
              : 'secondaryMarketOrderBlotter.cancellation.order.message.inProgress',
          ]}
          icon="autorenew"
        />
      );
    }
    case 'SUCCESS': {
      return (
        <AlertBanner
          color="primary"
          e2e="order-cancellation-alert-banner-success"
          className="position-absolute bottom-0 start-0 end-0"
          messages={[
            orderType === 'Live'
              ? 'secondaryMarketOrderBlotter.cancellation.liveTrade.message.success'
              : 'secondaryMarketOrderBlotter.cancellation.order.message.success',
          ]}
          icon="check_circle"
          dismissible
          onDismissed={() => onCancellationWorkflowCompleted(orderId)}
        />
      );
    }
    case 'REJECTED':
    case 'FAILED': {
      const message =
        error !== undefined
          ? [error]
          : status === 'REJECTED'
            ? ['secondaryMarketOrderBlotter.cancellation.message.rejected']
            : ['secondaryMarketOrderBlotter.cancellation.message.error'];
      return (
        <AlertBanner
          color="warning"
          e2e="order-cancellation-alert-banner-error"
          className="position-absolute bottom-0 start-0 end-0"
          messages={message}
          icon="error_outline"
          dismissible
          onDismissed={() => onCancellationWorkflowCompleted(orderId)}
        />
      );
    }
    default:
      return null;
  }
};

SecondaryOrderCancellationWorkflow.displayName = 'SecondaryOrderCancellationWorkflow';
